import { useMemo, useRef, useState } from 'react';
import { useUser } from '../auth/AuthProvider';
import { useNotifications, type AppNotification } from '../features/notifications/useNotifications';
import { navigate } from '../lib/router';
import { useUI } from '../ui/UIProvider';
import { Icon } from '../ui/Icon';
import { useDismiss } from '../ui/useDismiss';

const readKey = (userId: string) => `pcs-notification-read:${userId}`;
function loadRead(userId: string): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(readKey(userId)) || '[]')); } catch { return new Set(); }
}
function saveRead(userId: string, read: Set<string>) {
  try { localStorage.setItem(readKey(userId), JSON.stringify([...read].slice(-500))); } catch { /* storage unavailable */ }
}

export function Topbar({ section, title, onMenu, menuButtonRef }: { section: string; title: string; onMenu(): void; menuButtonRef: React.RefObject<HTMLButtonElement | null> }) {
  return (
    <header className="topbar">
      <button className="icon-button menu-toggle" aria-label="Open navigation" onClick={onMenu} ref={menuButtonRef}><Icon name="menu" size={20} /></button>
      <nav className="crumb" aria-label="Breadcrumb"><span>{section}</span><Icon name="chevronRight" size={14} /><b aria-current="page">{title}</b></nav>
      <div className="topbar-actions">
        <button className="icon-button" aria-label="Search clients" title="Search clients" onClick={() => { navigate('/clients'); setTimeout(() => document.getElementById('clientSearch')?.focus(), 50); }}><Icon name="search" /></button>
        <NotificationsMenu />
      </div>
    </header>
  );
}

function NotificationsMenu() {
  const user = useUser();
  const ui = useUI();
  const messages = useNotifications();
  const [open, setOpen] = useState(false);
  const [read, setRead] = useState(() => loadRead(user.id));
  const wrapRef = useRef<HTMLDivElement>(null);
  useDismiss(open, [wrapRef], () => setOpen(false));
  const unread = useMemo(() => messages.filter((message) => !read.has(message.id)), [messages, read]);

  function markRead(ids: string[]) {
    const next = new Set([...read, ...ids]);
    setRead(next); saveRead(user.id, next);
  }

  function choose(message: AppNotification) {
    setOpen(false);
    if (message.action.kind === 'armp-review') { ui.open('armpReview', { jobKey: message.action.jobKey }); return; }
    markRead([message.id]);
    if (message.action.kind === 'rodent-plan') { ui.open('rodentPlanRequest', { planId: message.action.planId, at: Date.now() }); navigate('/rodent-planning'); return; }
    navigate(message.action.path);
  }

  return (
    <div className="notification-wrap" ref={wrapRef}>
      <button className="icon-button notification" id="notificationButton" aria-label="Notifications" aria-expanded={open} aria-controls="notificationMenu" onClick={() => setOpen((value) => !value)}>
        <Icon name="bell" />{unread.length > 0 && <span className="notification-count">{unread.length > 9 ? '9+' : unread.length}</span>}
      </button>
      {open && (
        <section className="menu notification-menu" id="notificationMenu" aria-label="Notifications">
          <header>
            <div><b>Notifications</b><small id="notificationSubtitle">{unread.length ? `${unread.length} unread notification${unread.length === 1 ? '' : 's'}` : 'No unread notifications'}</small></div>
            {unread.length > 0 && <button className="btn-link" onClick={() => markRead(messages.map((message) => message.id))}>Mark all read</button>}
          </header>
          <div id="notificationItems">
            {messages.length ? messages.map((message) => (
              <button key={message.id} className={`notification-item${read.has(message.id) ? ' read' : ''}`} onClick={() => choose(message)}>
                <span className={`notification-symbol ${message.type}`} aria-hidden="true"><Icon name="alert" size={15} /></span>
                <span><b>{message.title}</b><small>{message.detail}</small></span>
              </button>
            )) : <p className="empty-state">You are up to date.</p>}
          </div>
        </section>
      )}
    </div>
  );
}
